/**
 * ── THE AREA-SELECTION CONTROL ──────────────────────────────────────────────────────
 *
 * Arm freehand, arm polygon, clear. Three affordances, and the third only when there is
 * something to clear.
 *
 * Built for the Analytics page's "Scans by location" card (VRG-480) and extracted here in
 * VRG-491, when the certificate page's Scans tab gained the same gesture. It is
 * PRESENTATIONAL — `value`/`onChange` for the armed mode, `hasArea`/`onClear` for the
 * committed one — because the two surfaces store the drawn ring in places that have
 * nothing to do with each other: the analytics ring is page-global redux (every card on
 * that page narrows to it), and the certificate tab's is component state (one list, one
 * map, one tab). A control that reached for `useSelector` could only ever serve the first.
 *
 * See `ScansByLocationDrawControl` for the redux-bound wrapper, which is all that is left
 * of the original after this extraction.
 *
 * ## §38 — the clear control is an `X`, not a trash can
 *
 * Clearing an area severs nothing and destroys no record; the scans it selected are
 * untouched. That is set-removal, and it is undone by drawing again — so it takes an `X`
 * and no confirmation.
 *
 * ## Gap + discoverability (user-reported, with a screenshot)
 *
 * Two separate findings, both from the same complaint — "gap between icons seems too
 * big / are the symbols discoverable enough":
 *
 * 1. **The gap.** §29 names TWO buckets, not one: `spacing={3}` (12px) for a button
 *    GROUP acting as a decision pair/cluster (Cancel/Save, a card-section header's
 *    actions), and `spacing={2}` (8px) for a body/inline-row gap, explicitly including
 *    "intentionally tight toolbars". This is the second bucket — three same-purpose
 *    icon buttons that plot as ONE control, not a set of independent actions — and it
 *    was shipped at the first bucket's 12px, which read looser than the group actually
 *    is. Confirmed the scale before touching the number: this renders under
 *    `apps/platform` → `VeraThemeProvider` → the `@repo/ui` 4px base
 *    (`packages/ui/theme/spacing.ts`), so `spacing={2}` is 8px here, not apps/app's 16.
 * 2. **Discoverability.** A lasso and a pentagon are not self-evident glyphs. Tooltips
 *    already existed; a leading "Draw:" label is added on top (the user's own
 *    suggestion) so the group reads as labelled furniture at a glance, without needing
 *    to hover first. Space allows both, so both ship.
 *
 * ## One badge, not four loose elements (VERA-260 follow-up, 2026-08-30)
 *
 * The gap/label fixes above stopped short of the actual complaint: "DRAW with the draw
 * icons should be in one badge vs separated". Gap tuning and a label cannot fix that on
 * their own — the label and each button were still FOUR independently painted things
 * (the canvas label had its own paper/border/shadow; each button had its OWN paper/
 * border/shadow too), so however tight the spacing, the eye still read four bordered
 * shapes in a row rather than one control.
 *
 * The fix moves the paper/border/shadow OFF every child and onto ONE outer container —
 * reusing the exact chrome already sitting on the same maps for the render-mode toggle
 * chip (`ScanMapPanel`'s top-right overlay: `background.paper` + a single `primary[100]`
 * border + `shadows[1]` + the shared radius), rather than inventing a fifth treatment
 * (§28/§69). `@repo/ui`'s `MENU_PAPER_SX`/`menuPaperChromeSx` were checked and are the
 * wrong reuse target here: they exist for POPOVER-style surfaces that open and close
 * above the page (a `<Menu>`, a bare `<Popover>`) and carry trigger-gap/max-height/scroll
 * rules this control has no use for — it is a persistent toolbar chip, the same category
 * as the zoom control and the render-mode chip beside it, not a dropdown.
 *
 * Internal separation is TWO vertical `Divider`s (the same `orientation="vertical"
 * flexItem` the bulk-selection toolbar already uses in `Table.tsx`, not a bespoke line) —
 * one after the label, one before the Clear button — plus padding, rather than gaps
 * between free-floating buttons. The lasso/polygon pair sits between the two dividers
 * with no divider of its own between them, because they are the same kind of action
 * (arm a drawing mode); Clear is a different kind of action (§38 — set-removal, not
 * mode-switching) and the second divider marks that boundary.
 *
 * Buttons carry no border/background/shadow of their own — the container supplies all
 * three — but the ACTIVE-mode fill (`brand[600]`, white icon) reads as a pressed pill
 * inside one bordered strip. Hover on an inactive button can safely use the ordinary
 * translucent `action.hover` (previously rejected — "lets the map through" — for a button
 * floating directly on map tiles; these buttons sit on the container's own solid
 * `background.paper`, so translucency reads as a normal hover state rather than a hole).
 */
import React from 'react'

import { Divider, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { LassoIcon, PentagonIcon, XIcon } from 'lucide-react'

import { CONTROL_HEIGHT, type MapDrawMode } from '@repo/ui'

/**
 * A chip floating over a map canvas is 40 — `CONTROL_HEIGHT`, the same token
 * `ScanMapPanel`'s own `MAP_CHIP_HEIGHT` resolves to, so the render-mode chip, the
 * certificate map's Journey chip and this one are one family BY CONSTRUCTION rather than
 * by three files independently hardcoding the same number. A 4px disagreement between two
 * chips at the same y on the same surface reads immediately as one of them being wrong.
 *
 * The `header` variant keeps 36, deliberately: it is not on the canvas. It sits inline in
 * the analytics card's header beside that card's own toggles, and its height answers to
 * them rather than to the map.
 */
const FLOATING_CHIP_HEIGHT = CONTROL_HEIGHT
const HEADER_CHIP_HEIGHT = 36

/**
 * The canvas-chip corner radius, in theme units — `2` is 8px on the `@repo/ui` 4px base.
 *
 * 8, NOT the 10 this control shipped with. The Journey chip on the certificate map IS a
 * shared `Button`, so it carries the Button's own 8px radius and cannot be talked out of
 * it; `ScanMapPanel`'s render-mode chip moved from 10 to 8 for exactly that reason. A
 * 10px draw chip would now be the only square-ish corner in a row of round ones — the
 * design system's value wins over the one-off.
 *
 * ⚠️ This is a THREE-file agreement (this control, `ScanMapPanel`'s render-mode chip, and
 * whatever the shared `Button` says). If the family value moves again, it moves in all of
 * them or none.
 */
const CHIP_RADIUS = 2
/**
 * A button INSIDE the chip steps one notch down from its container, the same
 * concentric-nesting ratio `MENU_ITEM_SX` uses for a row inside its paper. 1 = 4px against
 * the container's 8.
 */
const CHIP_BUTTON_RADIUS = 1

/**
 * Where the control is being rendered, which decides two things and only two: whether it
 * positions itself, and whether it casts a shadow.
 *
 * - `canvas` — floats over map tiles AND pins itself to the canvas's top-left. The
 *   original behaviour, and the right one when the control is the only thing in that
 *   corner.
 * - `overlay` — floats over map tiles but does NOT position itself, for a caller that is
 *   laying out a ROW of overlay chips and owns the coordinates. Added VRG-491 for the
 *   certificate page, whose Journey toggle shares the same corner (see `Scans.tsx`).
 * - `header` — inline, no shadow, for a card shell's action slot beside other controls.
 *
 * ⚠️ Exactly one instance per map, whichever variant. Two copies of an arm/disarm control
 * for one gesture is a control that can visibly disagree with itself.
 */
export type MapDrawControlVariant = 'canvas' | 'overlay' | 'header'

export interface MapDrawControlProps {
  /** The armed drawing mode. Ephemeral: which tool is up, not what is filtered. */
  value: MapDrawMode
  onChange: (next: MapDrawMode) => void
  /**
   * Whether a ring is currently COMMITTED. Drives the Clear affordance only — the control
   * never needs the ring itself, so it does not take one.
   */
  hasArea?: boolean
  /**
   * Clear the committed ring. Called after the control has already disarmed itself, so a
   * caller only has to drop its own ring.
   *
   * Required in practice whenever `hasArea` can be true: a Clear button is rendered from
   * `hasArea` alone, and one with nothing behind it is worse than none.
   */
  onClear?: () => void
  variant?: MapDrawControlVariant
}

export const MapDrawControl = ({
  value,
  onChange,
  hasArea = false,
  onClear,
  variant = 'header',
}: MapDrawControlProps) => {
  const theme = useTheme()

  // Chrome lives on the container (see the header's "One badge, not four loose
  // elements") — a button is just its icon on a transparent-or-active background.
  const buttonSx = (active: boolean) => ({
    width: 28,
    height: 28,
    borderRadius: CHIP_BUTTON_RADIUS,
    backgroundColor: active ? theme.palette.brand[600] : 'transparent',
    color: active ? theme.palette.common.white : theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: active ? theme.palette.brand[600] : theme.palette.action.hover,
      color: active ? theme.palette.common.white : theme.palette.text.primary,
    },
  })

  const toggle = (next: Exclude<MapDrawMode, 'off'>) => onChange(value === next ? 'off' : next)

  const floating = variant === 'canvas' || variant === 'overlay'
  const tooltipPlacement = floating ? 'right' : 'bottom'

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        height: floating ? FLOATING_CHIP_HEIGHT : HEADER_CHIP_HEIGHT,
        borderRadius: CHIP_RADIUS,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.primary[100]}`,
        // Only a floating variant sits on map tiles and needs to separate from them.
        boxShadow: floating ? theme.shadows[1] : 'none',
        pl: 3,
        pr: 2,
        ...(variant === 'canvas'
          ? { position: 'absolute', top: 12, left: 12, zIndex: 2 }
          : undefined),
      }}
    >
      <Typography
        variant="body3"
        color="text.secondary"
        sx={{ fontWeight: 600, whiteSpace: 'nowrap', mr: 3 }}
      >
        Draw:
      </Typography>

      <Divider orientation="vertical" flexItem sx={{ my: 1, mr: 2 }} />

      {/* Same kind of action — arm a drawing mode — so no divider between this pair;
          §29's TIGHT-TOOLBAR bucket (4px) for the gap between them, matching how close
          together a segmented pair inside one control should sit. */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip
          title={value === 'freehand' ? 'Cancel drawing (Esc)' : 'Draw an area to filter by'}
          placement={tooltipPlacement}
        >
          <IconButton
            size="small"
            aria-label="Draw an area on the map"
            aria-pressed={value === 'freehand'}
            onClick={() => toggle('freehand')}
            sx={buttonSx(value === 'freehand')}
          >
            <LassoIcon size={18} strokeWidth={value === 'freehand' ? 2 : 1.5} />
          </IconButton>
        </Tooltip>

        <Tooltip
          title={value === 'polygon' ? 'Cancel drawing (Esc)' : 'Place points; double-click to close'}
          placement={tooltipPlacement}
        >
          <IconButton
            size="small"
            aria-label="Draw an area point by point"
            aria-pressed={value === 'polygon'}
            onClick={() => toggle('polygon')}
            sx={buttonSx(value === 'polygon')}
          >
            <PentagonIcon size={18} strokeWidth={value === 'polygon' ? 2 : 1.5} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Only when there is an area to clear — a permanently-present disabled Clear is a
          record-state block, which §46 says to hide rather than disable. Clear is a
          DIFFERENT kind of action from the pair above (set-removal, §38, not a drawing
          mode), so it gets its own divider rather than joining their spacing. */}
      {hasArea && (
        <>
          <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 2 }} />
          <Tooltip title="Clear the drawn area" placement={tooltipPlacement}>
            <IconButton
              size="small"
              aria-label="Clear the drawn area"
              onClick={() => {
                onChange('off')
                onClear?.()
              }}
              sx={buttonSx(false)}
            >
              <XIcon size={18} strokeWidth={1.5} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Stack>
  )
}
